import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "@/lib/auth-context";
import { ROLE_LABELS, api } from "@/lib/api";
import { useT } from "@/lib/i18n-context";
import { ToThaiLogo } from "@/components/ToThaiLogo";
import {
  LayoutDashboard,
  ClipboardCheck,
  Wallet,
  Receipt,
  Ticket,
  Package,
  Truck,
  FileText,
  Bell,
  ShieldCheck,
  Settings,
  Users,
  ChefHat,
  Soup,
  ClipboardList,
  Store,
  ListChecks,
  LogOut,
  Menu,
  X,
  Banknote,
  ChartBar,
  Inbox,
  FolderTree,
  Database,
  Vault,
} from "lucide-react";

const NAV = [
  {
    section: "Główne", sectionKey: "sidebar.section.main",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", labelKey: "nav.dashboard", roles: "*" },
    ],
  },
  {
    section: "Operacje dzienne", sectionKey: "sidebar.section.daily",
    items: [
      { to: "/daily", icon: ClipboardCheck, label: "Rozliczenia dzienne", labelKey: "nav.daily", roles: ["owner", "manager", "waitress", "bartender", "receptionist", "accounting", "readonly"] },
      { to: "/safes", icon: Vault, label: "Sejf / Bank", labelKey: "nav.safes", roles: ["owner", "manager", "accounting"] },
      { to: "/expenses", icon: Receipt, label: "Wydatki", labelKey: "nav.expenses", roles: ["owner", "manager", "accounting"] },
      { to: "/vouchers", icon: Ticket, label: "Vouchery", labelKey: "nav.vouchers", roles: ["owner", "manager", "bartender", "receptionist"] },
      { to: "/shift-checklists", icon: ClipboardCheck, label: "Checklisty zmian", labelKey: "nav.checklists", roles: ["owner", "manager", "waitress", "bartender", "receptionist", "cook"] },
    ],
  },
  {
    section: "Zaopatrzenie", sectionKey: "sidebar.section.supply",
    items: [
      { to: "/orders", icon: ListChecks, label: "Zamówienia", labelKey: "nav.orders", roles: ["owner", "manager", "bartender", "cook", "warehouse", "accounting"] },
      { to: "/kitchen", icon: ChefHat, label: "Kuchnia (zamówienie)", labelKey: "nav.kitchen", roles: ["owner", "manager", "cook", "warehouse"] },
      { to: "/production", icon: Soup, label: "Produkcja (katalog)", labelKey: "nav.production", roles: ["owner", "manager", "accounting"] },
      { to: "/production/overview", icon: ClipboardList, label: "Produkcja — dziś", labelKey: "nav.production", roles: ["owner", "manager", "accounting", "warehouse"] },
      { to: "/production/report", icon: ClipboardCheck, label: "Raport produkcji", labelKey: "nav.productionReport", roles: ["owner", "manager", "cook", "warehouse"] },
      { to: "/products", icon: Package, label: "Produkty", labelKey: "nav.products", roles: ["owner", "manager", "accounting"] },
      { to: "/categories", icon: FolderTree, label: "Kategorie", labelKey: "nav.categories", roles: ["owner", "manager"] },
      { to: "/data-quality", icon: Database, label: "Jakość danych", labelKey: "nav.dataQuality", roles: ["owner", "manager"] },
      { to: "/suppliers", icon: Store, label: "Dostawcy", labelKey: "nav.suppliers", roles: ["owner", "manager", "accounting"] },
    ],
  },
  {
    section: "Ryzyko i kontrola", sectionKey: "sidebar.section.risk",
    items: [
      { to: "/alerts", icon: Bell, label: "Alerty", labelKey: "nav.alerts", roles: ["owner", "manager", "accounting"] },
      { to: "/audit", icon: ShieldCheck, label: "Audit log", labelKey: "nav.audit", roles: ["owner", "accounting"] },
    ],
  },
  {
    section: "Raporty i administracja", sectionKey: "sidebar.section.admin",
    items: [
      { to: "/settings", icon: Settings, label: "Ustawienia (progi legacy)", labelKey: "nav.settings", roles: ["owner"] },
      { to: "/recon-settings", icon: Settings, label: "Ustawienia rozliczeń", labelKey: "nav.reconSettings", roles: ["owner"] },
      { to: "/voucher-settings", icon: Ticket, label: "Ustawienia voucherów", labelKey: "nav.voucherSettings", roles: ["owner"] },
      { to: "/users", icon: Users, label: "Użytkownicy i uprawnienia", labelKey: "nav.users", roles: ["owner", "manager"] },
      { to: "/feedback", icon: Inbox, label: "Zgłoszenia od pracowników", labelKey: "nav.feedback", roles: ["owner"] },
    ],
  },
];

export function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();
  // P2c (2026-07-05) — badge licznika otwartych alertów dla owner/manager/accounting.
  // Polling co 30 sekund, zerowany po wizycie na /alerts (opcjonalne — badge się i tak zaktualizuje po następnym polling).
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    if (!user || !["owner", "manager", "accounting"].includes(user.role)) return;
    let cancelled = false;
    async function fetchCount() {
      try {
        const { data } = await api.get("/alerts/unread_count");
        if (!cancelled) setAlertCount(data?.count || 0);
      } catch { /* silent — nie blokujemy UI jeśli endpoint niedostępny */ }
    }
    fetchCount();
    const iv = setInterval(fetchCount, 30_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.role]);

  function isAllowed(item) {
    if (item.roles === "*") return true;
    return Array.isArray(item.roles) && user && item.roles.includes(user.role);
  }

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity lg:hidden ${open ? "opacity-100" : "pointer-events-none opacity-0"}`}
        onClick={onClose}
      />
      <aside
        className={`tt-sidebar fixed lg:static z-50 inset-y-0 left-0 w-72 flex flex-col transform transition-transform duration-200 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex-1 min-w-0">
            <ToThaiLogo layout="inline" size="sm" />
            <div className="text-[10px] uppercase tracking-widest text-[var(--tt-sidebar-muted)] mt-1">Control Center</div>
          </div>
          <button className="lg:hidden text-white/70 hover:text-white" onClick={onClose} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {NAV.map((section) => {
            const visible = section.items.filter(isAllowed);
            if (!visible.length) return null;
            return (
              <div key={section.section} className="mb-4">
                <div className="px-5 mb-2 text-[10px] uppercase tracking-widest text-[var(--tt-sidebar-muted)] font-semibold">
                  {section.sectionKey ? t(section.sectionKey, section.section) : section.section}
                </div>
                <ul className="space-y-1">
                  {visible.map((it) => {
                    const isAlerts = it.to === "/alerts";
                    const showBadge = isAlerts && alertCount > 0;
                    return (
                      <li key={it.to}>
                        <NavLink
                          to={it.to}
                          onClick={onClose}
                          className={({ isActive }) =>
                            `flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm font-medium border-l-2 border-transparent transition-colors ${
                              isActive ? "active" : ""
                            }`
                          }
                          data-testid={`nav-${it.to.replace("/", "")}`}
                        >
                          <it.icon className="h-4 w-4" />
                          <span className="flex-1">{it.labelKey ? t(it.labelKey, it.label) : it.label}</span>
                          {showBadge && (
                            <span
                              className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-red-500 text-white ring-2 ring-red-500/30"
                              data-testid="alerts-badge-count"
                              aria-label={`${alertCount} otwartych alertów`}
                            >
                              {alertCount > 99 ? "99+" : alertCount}
                            </span>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-4">
          <div className="mb-3 px-1">
            <div className="text-sm font-medium text-white truncate">{user?.full_name}</div>
            <div className="text-xs text-[var(--tt-sidebar-muted)]">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
          {/* P2g (2026-07-05) — globalny selektor języka. Zmiana wywołuje PATCH /users/me/language
              i odświeża profil (LanguageProvider → useT natychmiast reflektuje). */}
          <div className="mb-3 flex items-center gap-1.5">
            {["pl", "th", "ru"].map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                className={`flex-1 px-2 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${
                  lang === code
                    ? "bg-white text-[var(--tt-sidebar-bg,#1a1a1a)]"
                    : "bg-white/5 text-[var(--tt-sidebar-muted)] hover:bg-white/10 hover:text-white"
                }`}
                data-testid={`sidebar-lang-${code}`}
                aria-label={`${t("lang.change", "Zmień język")} — ${code.toUpperCase()}`}
                aria-pressed={lang === code}
              >
                {code === "pl" ? "PL" : code === "th" ? "ไทย" : "RU"}
              </button>
            ))}
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-white/10 hover:bg-white/15 text-white transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
            {t("common.logout", "Wyloguj")}
          </button>
        </div>
      </aside>
    </>
  );
}

export function MobileMenuButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="lg:hidden inline-flex items-center justify-center h-12 w-12 rounded-lg border border-[var(--tt-border)] bg-[var(--tt-surface)] hover:bg-[var(--tt-surface-2)] active:scale-95 transition-transform tt-focusable"
      aria-label="Otwórz menu"
      data-testid="mobile-menu-button"
    >
      <Menu className="h-6 w-6" />
    </button>
  );
}
